import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useBasketManager from "../../../../FinalizePage/hooks/useBasketManager";
import usePreBasket from "../../../../Universal/hooks/usePreBasket";
import { format } from "date-fns";
import { Assort } from "../../../../../models/Assort";
import getId from "../../../../../../utils/getId";
import { BasketItem } from "../../../../../models/BasketItem";
import { TicketAccess } from "../../../../../models/TicketAccess";
import { setAddToBasketMessage } from "../../../../../store/alerts/alertsActions";
import { State } from "../../../../../store";
import { FINALIZE_PAGE } from "../../../../../routes";
import useBatchAddToBasket from "../../../../Universal/PreBasketSummary/hooks/useBatchAddToBasket";
import { errorMessageFromApi } from "../../../../../utils/apiErrorMessages";

export default function useCourtTicketSelection(
  exerciseId: number,
  ticketAccess: TicketAccess,
  ticketLimit: number,
  onTicketsAdded: () => void,
) {
  const { addToBasket } = useBatchAddToBasket();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);

  const { preBasket, addItemToBasket, removeItemFromBasket, clearBasket, setItemQuantity } = usePreBasket();
  const { getBasket, basket } = useBasketManager();

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [alert, setAlert] = useState<string>("");
  const [isSavingBasket, setIsSavingBasket] = useState<boolean>(false);

  useEffect(() => {
    setAlert("");
  }, [exerciseId]);

  const formatTimeRange = (startTime: number, endTime: number) => {
    const startFormatted = format(startTime, "HH:mm");
    const endFormatted = format(endTime, "HH:mm");
    return `${startFormatted} - ${endFormatted}`;
  };

  const getQnt = (value: Assort): number => {
    const preBasketQnt = preBasket
      .filter(
        (item) =>
          item.assortIds === getId(value) &&
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    const basketQnt = basket
      .filter(
        (item) =>
          item.assortIds === getId(value) &&
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    return preBasketQnt + basketQnt;
  };

  const ticketsInCurrentBasket = () => {
    const preBasketCount = preBasket
      .filter(
        (item) =>
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    const basketCount = basket
      .filter(
        (item) =>
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    return preBasketCount + basketCount;
  };

  const getEffectiveMax = (item: Assort) => {
    let effectiveMax = item.maxSale || 99;

    if (ticketLimit || ticketLimit === 0) {
      const currentQnt = getQnt(item);
      const totalTicketsInBaskets = ticketsInCurrentBasket();
      const remainingTickets = ticketLimit - totalTicketsInBaskets + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingTickets);
    }

    return Math.max(0, effectiveMax);
  };

  const handleQuantityChange = (item: Assort, newValue: number) => {
    if (setItemQuantity) {
      setItemQuantity(item, newValue, ticketAccess);
    }
  };

  const sumBasket = () => preBasket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);

  const addBasketRequest = async () => {
    try {
      // Użyj batch API - automatycznie wykryje czy backend obsługuje nowy endpoint
      // i zrobi fallback na stare API jeśli potrzeba (bez 404 dla użytkownika)
      const result = await addToBasket(preBasket);

      if (result.isError) {
        setAlert(errorMessageFromApi(result));
        return false;
      } else {
        finalizeFunction();
        return true;
      }
    } catch (error) {
      setAlert("Wystąpił błąd podczas dodawania do koszyka");
      return false;
    }
  };

  const finalizeFunction = () => {
    // Wywołaj callback dla odświeżenia danych
    onTicketsAdded();

    // Sprawdź konfigurację popup
    if (saleConfiguration.popupAfterAddTicket === true) {
      dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
    }

    if (saleConfiguration.popupAfterAddTicket === false) {
      setTimeout(() => {
        navigate(FINALIZE_PAGE);
      }, 100);
    }
  };

  const saveBasket = async () => {
    setIsSavingBasket(true);
    try {
      const success = await addBasketRequest();
      if (success) {
        clearBasket();
        await getBasket();
      }
    } finally {
      setIsSavingBasket(false);
    }
  };

  return {
    formatTimeRange,
    getQnt,
    getEffectiveMax,
    handleQuantityChange,
    addItemToBasket,
    removeItemFromBasket,
    sumBasket,
    saveBasket,
    alert,
    setAlert,
    openTooltip,
    setOpenTooltip,
    preBasket,
    ticketsInCurrentBasket,
    isSavingBasket,
  };
}
